import type { Lucid, Script, Tx, Validator } from 'lucid-cardano';
import type { CardanoContracts, Market } from './contract';
import { reportToSentry } from '$lib/sentry';
import type { SortOption } from '$lib/explore/type';

export async function submitTransaction(incompleteTx: Tx) {
	try {
		const tx = await incompleteTx.complete();
		const signedTx = await tx.sign().complete();
		const txHash = await signedTx.submit();
		return txHash;
	} catch (error) {
		reportToSentry({ error, message: 'Failed to submit transaction' });
		return error;
	}
}

export async function getAuctionUtxos(
	lucid: Lucid,
	contracts: CardanoContracts
) {
	const auctionValidator = getAuctionValidator(contracts);
	const auctionAddress = lucid.utils.validatorToAddress(auctionValidator);

	try {
		return await lucid.utxosAt(auctionAddress);
	} catch (error) {
		reportToSentry({ error, message: 'Error fetching utxos' });
	}

	return [];
}

function getAuctionValidator(
	contracts: CardanoContracts,
	isPrivate = false
): Validator {
	if (isPrivate) {
		return {
			type: 'PlutusV2',
			script: contracts.private_auction_validator
		};
	} else {
		return {
			type: 'PlutusV2',
			script: contracts.public_auction_validator
		};
	}
}

export function getAuctionValidatorAddress(lucid: Lucid, market: Market) {
	return lucid.utils.validatorToAddress(
		asPlutusV2Script(market.contracts.public_auction_validator)
	);
}

export function getFinsetValidatorAddress(lucid: Lucid, market: Market) {
	return lucid.utils.validatorToAddress(
		asPlutusV2Script(market.contracts.auction_finset_validator)
	);
}

export function asPlutusV2Script(scriptCborHex: string): Script {
	return {
		type: 'PlutusV2',
		script: scriptCborHex
	};
}

const DEFAULT_BROWSE_OPTIONS = [
	{
		label: 'Ending Soon',
		urlParam: 'time',
		column: 'close_time',
		order: 'asc'
	},
	{
		label: 'Price (High to Low)',
		urlParam: 'price',
		column: 'current_price',
		order: 'desc'
	},
	{
		label: 'Price (Low to High)',
		urlParam: 'price',
		column: 'current_price',
		order: 'asc'
	},
	{
		label: 'Title (A-Z)',
		urlParam: 'title',
		column: 'first_nft_name',
		order: 'asc'
	},
	{
		label: 'Title (Z-A)',
		urlParam: 'title',
		column: 'first_nft_name',
		order: 'desc'
	},
	{
		label: 'Trending',
		urlParam: 'trending',
		column: 'most_recent_bid_time',
		order: 'asc'
	},
	{
		label: 'Most Bids',
		urlParam: 'bids',
		column: 'total_bids',
		order: 'desc'
	}
];

export function mapSortOptions(activeColumn: string, ascending: boolean) {
	return DEFAULT_BROWSE_OPTIONS.map((option) => {
		const orderAscending = option.order === 'asc';
		const toggled =
			option.column === activeColumn && orderAscending === ascending;
		return { ...option, toggled } as SortOption;
	});
}
